import { localState, sessionState } from 'src/app/core/services/signal-state.storage';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { AppService } from 'src/app/app.service';
import { PaymentMethodInfo } from 'src/app/shared/Models/Interfaces';
import * as dropin from 'braintree-web-drop-in';
import { loadStripe, Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js';
import {
  cc_id,
  transformIntoApiCallObj,
} from 'src/app/gdo/gdo-shared/Models/Constants';
import { StatusDTO } from 'src/app/gdo/gdo-shared/Models/Models';
import { GdoStepCompletionStatusComponent } from 'src/app/gdo/gdo-shared/gdo-shared-components/gdo-step-completion-status/gdo-step-completion-status.component';
import { take } from 'rxjs/operators';

interface PaymentFormData {
  billingAddress?: string | null;
  city?: string | null;
  selectedState?: string | null;
  zipCode?: string | null;
  email?: string | null;
}

interface PaymentNavigationState {
  formData?: PaymentFormData;
  isDefaultAddress?: boolean;
  isDefaultPayment?: boolean;
}

@Component({
    selector: 'app-gdo-braintree-integration',
    templateUrl: './gdo-braintree-integration.component.html',
    styleUrls: ['./gdo-braintree-integration.component.scss'],
    standalone: false
})
export class GdoBraintreeIntegrationComponent implements OnInit {
  titleBtn = 'Save';
  formData: PaymentFormData | null = null;
  state: PaymentNavigationState = {};
  isDefaultAddress: boolean = false;
  isDefaultPayment: boolean = false;
  localItem: string | null;
  paymentOptions: PaymentMethodInfo[];
  paymentOptionsLength: number | null = null;
  isWebView: Boolean = false;
  isAcura: Boolean = false;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private service: AppService,
    private location: Location
  ) {
    this.isWebView = this.service.getWebviewCurrentStatus()
    this.localItem = sessionState.getItem('paymentOptions');
    if (this.localItem == null) {
      this.paymentOptions = [];
    } else {
      this.paymentOptions = JSON.parse(this.localItem);
    }
  }

  // ── Braintree state ────────────────────────────────────────────────────────
  braintreeIsReady = false;
  dropIninstance: dropin.DropinInstance | null = null;
  nonceKey: string | null = null;

  // ── Stripe state ────────────────────────────────────────────────────────────
  paymentProvider: string = 'braintree';
  stripeInstance: Stripe | null = null;
  stripeElements: StripeElements | null = null;
  stripeCardElement: StripeCardElement | null = null;
  stripeIsReady: boolean = false;
  stripeClientSecret: string | null = null;
  stripeCustomerId: string | null = null;
  stripeCardBrand: string = 'unknown';

  // ── Migration detection (Gap 1) ────────────────────────────────────────────
  hasMigratedLegacyCard: boolean = false;

  ngOnInit() {
    this.isWebView = this.service.getWebviewCurrentStatus();
    this.setIsAcuraLocally();
    this.titleBtn = 'Save';
    this.checkForLegacyTokens();
    this.initPaymentUI();
    this.state = readPaymentNavigationState(this.location.getState());
    this.formData = this.state.formData ?? null;
    this.isDefaultAddress = this.state.isDefaultAddress ?? false;
    this.isDefaultPayment = this.state.isDefaultPayment ?? false;
    
  }

  private checkForLegacyTokens(): void {
    const isLegacyBraintree = (id: string) => !!id && !(id.startsWith('pm_') && id.includes(':'));
    const isStripeToken = (id: string) => !!id && id.startsWith('pm_') && id.includes(':');

    // DPI-1921: If user already has a Stripe card, migration is complete — skip banner
    if (this.paymentOptions?.length > 0) {
      if (this.paymentOptions.some(pm => isStripeToken(pm.bill_agreement_id))) return;

      this.hasMigratedLegacyCard = this.paymentOptions.some(
        pm => isLegacyBraintree(pm.bill_agreement_id)
      );
      if (this.hasMigratedLegacyCard) return;
    }

    const payload = transformIntoApiCallObj(
      { filter_status: 1, payments_returned: 5 },
      'get_acct_payment_methods_and_terms_m'
    );
    this.service.AriaPassThroughConnect(payload).subscribe((res: any) => {
      if (res?.error_code === 0 && res?.account_payment_methods?.length > 0) {
        if (res.account_payment_methods.some((pm: any) => isStripeToken(pm.bill_agreement_id))) return;

        this.hasMigratedLegacyCard = res.account_payment_methods.some(
          (pm: any) => isLegacyBraintree(pm.bill_agreement_id)
        );
      }
    });
  }

  initPaymentUI() {
    this.service.getPaymentConfig().subscribe({
      next: (cfg) => {
        this.paymentProvider = cfg.provider || 'braintree';
        if (this.paymentProvider === 'stripe') {
          this.initStripe(cfg.stripePublishableKey);
        } else {
          this.getTokenFromApi();
        }
      },
      error: (err) => {
        
        this.getTokenFromApi();
      },
    });
  }

  private async initStripe(publishableKey: string): Promise<void> {
    if (!publishableKey) {
      console.error('Stripe publishable key is missing.');
      return;
    }
    try {
      this.stripeInstance = await loadStripe(publishableKey);
      if (!this.stripeInstance) { return; }
      const accData = this.service.getAccDetails();
      // Gap 3: reuse existing cus_xxx (pm_xxx:cus_xxx format) to avoid duplicate Customer
      const existingCustomerId = this.paymentOptions
        ?.map(pm => pm.bill_agreement_id)
        ?.find(id => id?.startsWith('pm_') && id?.includes(':'))
        ?.split(':')[1] ?? null;
      this.service.createSetupIntent(accData?.acctNo, accData?.email, existingCustomerId ?? undefined, accData?.firstName, accData?.lastName).subscribe({
        next: ({ client_secret, customerId }) => {
          this.stripeClientSecret = client_secret;
          this.stripeCustomerId = customerId;
          this.mountStripeElement();
        },
        error: () => console.error('SetupIntent creation failed.'),
      });
    } catch (err) {
      console.error('Stripe.js failed to load.');
    }
  }

  private mountStripeElement(): void {
    if (!this.stripeInstance) { return; }
    this.stripeElements = this.stripeInstance.elements();
    this.stripeCardElement = this.stripeElements.create('card', { disableLink: true,
      style: {
        base: {
          fontFamily: 'Roboto, Arial, sans-serif',
          fontSize: '16px',
          color: this.isAcura ? '#ffffff' : '#003366',
        },
      },
    });
    // Track card brand as user types for cc_id mapping sent to Aria
    this.stripeCardElement.on('change', (event: any) => {
      if (event.brand && event.brand !== 'unknown') {
        this.stripeCardBrand = event.brand;
      }
    });
    this.stripeCardElement.mount('#dropin-container');
    this.stripeIsReady = true;
  }

  setIsAcuraLocally() {
    let vehicleData = sessionState.getItem("vehicleMake");
    if (vehicleData) {
      vehicleData = vehicleData.toLowerCase();
      if (vehicleData == 'acura') {
        this.isAcura = true;
      }
    }
  }

  private generateDropInUI(clientToken: string): void {
    const config = this.getDropinConfig(clientToken);
    dropin.create(config, (err, dropinInstance) => {
      if (err || !dropinInstance) {
        // Handle any errors that might've occurred when creating Drop-in
        console.error('Braintree drop-in failed to initialize.');
        
        return;
      }
      this.dropIninstance = dropinInstance;
      this.braintreeIsReady = true;
      this.dropIninstance.on('noPaymentMethodRequestable', () => {
        
        this.resetTitle();
      });
    });
  }
  private resetTitle() {
    this.titleBtn = 'Save';
  }

  private getDropinConfig(clientToken: string): any {
    return {
      authorization: clientToken,
      container: '#dropin-container',
    };
  }

  getTokenFromApi() {
    this.service.generateToken().subscribe(
      (res: any) => {
        if (res.success === true) {
          this.generateDropInUI(res?.clientToken);
        }
      },
      (error) => {
        
      }
    );
  }

  pay() {
    if (this.paymentProvider === 'stripe') {
      this.payWithStripe();
    } else {
      this.payWithBraintree();
    }
  }

  private async payWithStripe(): Promise<void> {
    if (!this.stripeInstance || !this.stripeCardElement || !this.stripeClientSecret) {
      console.error('Stripe is not ready.');
      return;
    }

    // "Proceed to checkout" — card already confirmed; retrieve PM details and update Aria
    if (this.titleBtn === 'Proceed to checkout') {
      if (this.nonceKey) {
        // nonceKey = pm_xxx:cus_xxx (Aria API doc format) — pm is index [0]
        const pmId = this.nonceKey.includes(':') ? this.nonceKey.split(':')[0] : this.nonceKey;
        this.service.stripeRetrievePaymentMethod(pmId).subscribe({
          next: (pmDetails) => {
            const accData = this.service.getAccDetails();
            const hasAriaAccount = !!accData?.acctNo;
            if (hasAriaAccount) {
              this.addNewPaymentMethodStripe(pmDetails);
            } else {
              
            }
          },
          error: () => console.error('Failed to retrieve Stripe PaymentMethod details.'),
        });
      }
      return;
    }

    const { setupIntent, error } = await this.stripeInstance.confirmCardSetup(
      this.stripeClientSecret,
      { payment_method: { card: this.stripeCardElement } }
    );

    if (error) {
      console.error('Stripe confirmCardSetup error.');
      return;
    }

    if (setupIntent && setupIntent.status === 'succeeded') {
      const pmId = setupIntent.payment_method as string;
      // Aria API doc format: pm_xxx:cus_xxx (PM first, then customer)
      this.nonceKey = this.stripeCustomerId ? `${pmId}:${this.stripeCustomerId}` : pmId;
      this.titleBtn = 'Proceed to checkout';
    }
  }

  /** Builds the Aria update_acct_billing_group_m payload from Stripe PM details (GDO). */
  private async addNewPaymentMethodStripe(pmDetails: { brand: string; last4: string; exp_month: number; exp_year: number }): Promise<void> {
    let accData: any = this.service.getAccDetails();
    let billingInfo = accData?.billingGroupsInfo?.[0];

    // DPI-1345: Read collection group — fall back to live Aria call if not in session
    let collectionGroupId: string | null =
      accData?.collectionAcctGroup?.[0]?.clientCollectionsAcctGroupId ?? null;

    if (!collectionGroupId || !billingInfo?.billingGroupNo) {
      try {
        const req = transformIntoApiCallObj({ include_billing_groups: 1 }, 'get_acct_details_all_m');
        const res: any = await this.service.AriaPassThroughConnect(req).toPromise();
        if (res?.error_code === 0) {
          if (!billingInfo?.billingGroupNo && res?.billing_groups_info?.length > 0) {
            billingInfo = { billingGroupNo: res.billing_groups_info[0].billing_group_no };
          }
          if (res?.collection_acct_group?.length > 0) {
            collectionGroupId = res.collection_acct_group[0].client_collections_acct_group_id ?? null;
          }
        }
      } catch (e) {
        console.error('[GDO Stripe] Could not fetch account details.');
      }
    }

    if (collectionGroupId && collectionGroupId !== 'US_Stripe_Collection_Group') {
      try {
        const switchRes: any = await this.service
          .switchToStripeCollectionGroupIfNeeded(collectionGroupId, accData?.acctNo ?? null)
          .toPromise();
        if (switchRes?.error_code !== 0 && !switchRes?.already_stripe) {
          throw new Error(switchRes?.error_msg ?? 'Collection group switch failed');
        }
      } catch (e) {
        console.error('[GDO Stripe] Collection group switch failed - aborting.');
        let statusData: StatusDTO = {
          title: 'Error Occurred',
          details: 'Unable to switch payment processor. Please try again.',
          descriptionHtml: `<p>There seems to be an issue. Kindly try again</p>`,
          stepSuccessful: false,
        };
        this.dialog.open(GdoStepCompletionStatusComponent, { height: '45vh', width: '40vw', data: statusData });
        return;
      }
    }

    let data: any = {
      bill_agreement_id: this.nonceKey, // pm_xxx:cus_xxx
      billing_group_no: billingInfo?.billingGroupNo,
      bill_first_name: accData?.firstName,
      bill_last_name: accData?.lastName,
      bill_address1: this.formData?.billingAddress,
      bill_city: this.formData?.city,
      bill_state_prov: this.formData?.selectedState,
      bill_country: accData?.countryCode ?? 'US',
      bill_postal_cd: this.formData?.zipCode,
      bill_phone: accData?.phone,
      bill_cell_phone: accData?.cellPhone,
      bill_work_phone: accData?.workPhone,
      bill_email: this.formData?.email,
      pay_method_name: 'Credit Card',
      pay_method_description: 'Credit Card',
      pay_method_type: 13,
      cc_id: evaluateStripeCardBrand(pmDetails.brand),
    };

    if (this.isDefaultPayment) {
      let lastPrimaryPaymentMethodNo = Number(sessionState.getItem('paymentMethodNo'));
      if (lastPrimaryPaymentMethodNo < 0) { lastPrimaryPaymentMethodNo = 0; }
      data.primary_payment_method_no = lastPrimaryPaymentMethodNo + 1;
    } else {
      data.primary_payment_method_no = Number(sessionState.getItem('defaultPaymentMethodNo'));
    }

    data = transformIntoApiCallObj(data, 'update_acct_billing_group_m');
    this.service.AriaPassThroughConnect(data).subscribe((response) => {
      if (response && response.error_code === 0) {
        this.service.setNewPaymentMethodInfo(null);
        data.suffix = pmDetails.last4;
        data.cc_type = pmDetails.brand;
        data.cc_expire_mm = pmDetails.exp_month;
        data.cc_expire_yyyy = pmDetails.exp_year;
        this.paymentOptions.push(data);
        sessionState.setItem('paymentOptions', JSON.stringify(this.paymentOptions));
        this.onBack();
      } else {
        let statusData: StatusDTO = {
          title: 'Error Occurred',
          details: response?.error_msg ?? 'Unable to save payment method.',
          descriptionHtml: `<p>There seems to be an issue. Kindly try again</p>`,
          stepSuccessful: false,
        };
        this.dialog.open(GdoStepCompletionStatusComponent, {
          height: '45vh',
          width: '40vw',
          data: statusData,
        });
      }
    });
  }

  private payWithBraintree(): void {
    if (this.titleBtn === 'Proceed to checkout') {
      if (this.nonceKey) {
        let accData = this.service.getAccDetails();
        let hidasId = sessionState.getItem('userId');
        this.service
          .createVault(this.nonceKey, this.formData, accData)
          .subscribe(
            (res: any) => {
              
              this.addNewPaymentMethod(res);
            },
            (error) => {
              
            }
          );
      }
      return;
    }
    if (this.dropIninstance?.isPaymentMethodRequestable()) {
      this.dropIninstance.requestPaymentMethod((err, payload) => {
        if (err) {
          
        } else {
          this.unsubscribePaymentMethodRequestable();
          
          this.nonceKey = payload.nonce;
          this.titleBtn = 'Proceed to checkout';
        }
      });
    }
  }
  addNewPaymentMethod(res: any) {
    if (res && res.success) {
      
      let creditCard; 
      if(res.customer){
        creditCard = res?.customer?.creditCards?.[0]; // when user has more than one credit card
      }else{
        creditCard = res.creditCard; //initial credit card 
      }
      // let billingAddress = creditCard?.billingAddress;
      // let cardType = creditCard?.cardType;
      // let expirationMonth = creditCard?.expirationMonth;
      // let expirationYear = creditCard?.expirationMonth;
      let token = creditCard?.token;
      

      let accData: any = this.service.getAccDetails();
      let billingInfo = accData?.billingGroupsInfo?.[0];

      

      let cardType = res.customer? res?.customer?.creditCards?.[0].cardType: creditCard.cardType;
      let data: any = {
        bill_agreement_id: token,
        billing_group_no: billingInfo?.billingGroupNo,
        bill_first_name: accData?.firstName,
        bill_last_name: accData?.lastName,
        bill_address1: this.formData?.billingAddress,
        bill_city: this.formData?.city,
        bill_state_prov: this.formData?.selectedState,
        bill_country: accData?.countryCode ?? 'US',
        bill_postal_cd: this.formData?.zipCode,
        bill_phone: accData?.phone,
        bill_cell_phone: accData?.cellPhone,
        bill_work_phone: accData?.workPhone,
        bill_email: this.formData?.email,
        pay_method_name: 'Credit Card',
        pay_method_description: 'Credit Card',
        pay_method_type: 13,
        cc_id: evaluateCardType(cardType),
        // primary_payment_method_no: 2
      };
      if (this.isDefaultPayment) {
        let lastPrimaryPaymentMethodNo = Number(
          sessionState.getItem('paymentMethodNo')
        );
        if (lastPrimaryPaymentMethodNo < 0) {
          lastPrimaryPaymentMethodNo = 0;
        }
        data.primary_payment_method_no = lastPrimaryPaymentMethodNo + 1;
      } else {
        data.primary_payment_method_no = Number(
          sessionState.getItem('defaultPaymentMethodNo')
        );
      }

      

      data = transformIntoApiCallObj(data, 'update_acct_billing_group_m');
      // NOTE: TODO replace following aria pass connect api with wrapper once wrapper returns all information
      this.service.AriaPassThroughConnect(data).subscribe((response) => {
        
        if (response && response.error_code === 0) {
          
          this.service.setNewPaymentMethodInfo(null);
          this.paymentOptions.push(data);
          sessionState.setItem('paymentOptions', JSON.stringify(this.paymentOptions));
          this.onBack();
        } else {
          //
          let statusData: StatusDTO = {
            title: 'Error Occurred',
            details: response?.error_msg,
            descriptionHtml: `<p>There seems to be an issue. Kindly try again</p>`,
            stepSuccessful: false,
          };
          let ref = this.dialog.open(GdoStepCompletionStatusComponent, {
            height: '45vh',
            width: '40vw',
            data: statusData,
          });
          ref.afterClosed().pipe(take(1)).subscribe((result) => {
            // this.titleBtn = 'Save';
            // this.getTokenFromApi();
            // this.state = this.location.getState();
            // this.formData = this.state.formData
            // this.isDefaultAddress = this.state.isDefaultAddress
            // this.isDefaultPayment = this.state.isDefaultPayment
            // 
            // this.router.navigate(['/payment/addNewPayment'], { replaceUrl: true });
          });
        }
      });
    } else if (res?.errors && !res.success) {
      let statusData: StatusDTO = {
        title: 'Error Occurred',
        details: res?.message,
        descriptionHtml: `<p>There seems to be an issue. Kindly try again</p>`,
        stepSuccessful: false,
      };
      let ref = this.dialog.open(GdoStepCompletionStatusComponent, {
        height: '45vh',
        width: '40vw',
        data: statusData,
      });
      ref.afterClosed().pipe(take(1)).subscribe((result) => {
        // this.router.navigate(['/payment/addNewPayment'], { replaceUrl: true });
      });
    }
  }

  private unsubscribePaymentMethodRequestable() {
    if (this.dropIninstance) {
      this.dropIninstance.off('paymentMethodRequestable', {});
    }
  }

  onBack() {
    if (this.service.getWebviewCurrentStatus()) {
      this.router.navigate(['/gdoWV/purchase-subscription'], {
        replaceUrl: true,
      });
    } else {
      let fromSubscription =
        this.service.getAddingPaymentMehodFromSubscription();
      if (fromSubscription && fromSubscription == 'true') {
        this.router.navigate(['gdo-product/manage-billing'], {
          replaceUrl: true,
        });
      } else {
        this.router.navigate(['/billing'], { replaceUrl: true });
      }
    }
  }

  onCardAddBack() {
    if (this.service.getWebviewCurrentStatus()) {
      this.router.navigate(['/gdoWV/addNewPaymentOption'], {
        replaceUrl: true,
      });
    } else {
      let fromSubscription =
        this.service.getAddingPaymentMehodFromSubscription();
      if (fromSubscription && fromSubscription == 'true') {
        this.router.navigate(['gdo-product/manage-billing'], {
          replaceUrl: true,
        });
      } else {
        this.router.navigate(['/billing/addNewPaymentOption'], { replaceUrl: true });
      }
    }
  }

  addTempCardData() {
    const paymentCard: PaymentMethodInfo = {
      bill_first_name: 'Mark',
      bill_last_name: 'Aizaz',
      bill_address1: '999 West Street',
      bill_city: 'New York',
      bill_state_prov: 'NY',
      bill_country: 'US',
      bill_postal_cd: '15745',
      bill_phone: '7142035236',
      bill_cell_phone: '444-555-6666',
      bill_work_phone: '1112223333',
      bill_email: 'markaizaz@ariatest.com',
      pay_method_name: 'Credit Card',
      pay_method_description: 'Credit Card',
      pay_method_type: 13,
      suffix: '0004',
      cc_expire_mm: 5,
      cc_expire_yyyy: 2022,
      cc_type: 'Visa',
      bill_agreement_id: '68nyfx6',
      status: 1,
      payment_method_no: 2,
      persistent_ind: 1,
      from_date: '2022-03-16 07:43:36',
      cc_prefix: '400551',
      is_default: false,
    };
    if (this.paymentOptions?.length > 0) {
      paymentCard.is_default = false;
    }
    this.paymentOptions.push(paymentCard);
    localState.setItem('paymentOptions', JSON.stringify(this.paymentOptions));
  }
}

function evaluateCardType(cardType: String) {
  const card_type = cc_id.find((x) => x.displayValue === cardType);
  if (card_type) {
    return card_type.value;
  } else return 1;
}

/**
 * Maps Stripe card brand strings (lowercase) to Aria cc_id numeric values.
 * Stripe brands: 'visa' | 'mastercard' | 'amex' | 'discover' | 'diners' | 'jcb' | 'unionpay' | 'unknown'
 */
function evaluateStripeCardBrand(brand: string): number {
  const map: Record<string, number> = {
    visa: 1,
    mastercard: 2,
    amex: 3,
    discover: 4,
    diners: 5,
  };
  return map[brand?.toLowerCase()] ?? 1;
}

function readPaymentNavigationState(value: unknown): PaymentNavigationState {
  if (typeof value !== 'object' || value === null) return {};

  const state = value as Record<string, unknown>;
  const navigationState: PaymentNavigationState = {};
  if (isPaymentFormData(state.formData)) {
    navigationState.formData = state.formData;
  }
  if (typeof state.isDefaultAddress === 'boolean') {
    navigationState.isDefaultAddress = state.isDefaultAddress;
  }
  if (typeof state.isDefaultPayment === 'boolean') {
    navigationState.isDefaultPayment = state.isDefaultPayment;
  }
  return navigationState;
}

function isPaymentFormData(value: unknown): value is PaymentFormData {
  if (typeof value !== 'object' || value === null) return false;
  const formData = value as Record<string, unknown>;
  return ['billingAddress', 'city', 'selectedState', 'zipCode', 'email'].every((key) => {
    const field = formData[key];
    return field === undefined || field === null || typeof field === 'string';
  });
}
